import Cookies from "js-cookie";

// Nazwa ciasteczka przechowującego szczegółowe ustawienia
const COOKIE_DETAILS_NAME = "cookie_consent_details";

// Typy zgód w Google Consent Mode v2
const CONSENT_TYPES = {
	AD_STORAGE: "ad_storage",
	AD_USER_DATA: "ad_user_data",
	AD_PERSONALIZATION: "ad_personalization",
	ANALYTICS_STORAGE: "analytics_storage",
	FUNCTIONALITY_STORAGE: "functionality_storage",
	SECURITY_STORAGE: "security_storage",
};

// Wzorce nazw plików cookie analitycznych do usunięcia
const ANALYTICS_COOKIE_PATTERNS = [
	"_ga",
	"_gid",
	"_gat",
	"__utm",
	"collect",
	"analytics",
];

// Pobierz wartość zgody z cookie
const getConsentValue = () => {
	return Cookies.get("cookie_consent") || null;
};

// Odczytaj zapisane niestandardowe ustawienia z ciasteczka
const getStoredCustomSettings = () => {
	const detailsCookie = Cookies.get(COOKIE_DETAILS_NAME);
	if (!detailsCookie) {
		return null;
	}
	try {
		const settings = JSON.parse(detailsCookie);
		// Prosta walidacja - sprawdźmy czy są oczekiwane klucze
		if (
			typeof settings[CONSENT_TYPES.AD_STORAGE] === "boolean" &&
			typeof settings[CONSENT_TYPES.AD_USER_DATA] === "boolean" &&
			typeof settings[CONSENT_TYPES.AD_PERSONALIZATION] === "boolean" &&
			typeof settings[CONSENT_TYPES.ANALYTICS_STORAGE] === "boolean" &&
			typeof settings[CONSENT_TYPES.FUNCTIONALITY_STORAGE] ===
				"boolean" &&
			typeof settings[CONSENT_TYPES.SECURITY_STORAGE] === "boolean"
		) {
			return settings;
		}
		return null; // Nieprawidłowa struktura
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error("Error parsing cookie consent details:", e);
		return null; // Błąd parsowania JSON
	}
};

// Aktualizuj zgody w Google Consent Mode
const updateGoogleConsent = (consentSettings) => {
	if (window.gtag) {
		window.gtag("consent", "update", consentSettings);
	}
};

// Usuń pliki cookie związane z analityką
const removeAnalyticsCookies = () => {
	const domain = window.location.hostname;

	// Pobierz wszystkie dostępne cookies
	const allCookies = Cookies.get();

	// Przeiteruj przez wszystkie cookies
	Object.keys(allCookies).forEach((cookieName) => {
		// Sprawdź, czy nazwa cookie zawiera któryś z wzorców analitycznych
		const isAnalyticsCookie = ANALYTICS_COOKIE_PATTERNS.some((pattern) =>
			cookieName.toLowerCase().includes(pattern.toLowerCase())
		);

		if (isAnalyticsCookie) {
			// Usuń cookie na różnych poziomach
			Cookies.remove(cookieName);
			Cookies.remove(cookieName, { path: "/" });
			Cookies.remove(cookieName, { domain: `.${domain}` });
			Cookies.remove(cookieName, { domain: `.${domain}`, path: "/" });
		}
	});
};

// Ustaw pełną zgodę
const grantFullConsent = () => {
	const consentSettings = {
		[CONSENT_TYPES.AD_STORAGE]: "granted",
		[CONSENT_TYPES.AD_USER_DATA]: "granted",
		[CONSENT_TYPES.AD_PERSONALIZATION]: "granted",
		[CONSENT_TYPES.ANALYTICS_STORAGE]: "granted",
		[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: "granted",
		[CONSENT_TYPES.SECURITY_STORAGE]: "granted",
	};

	updateGoogleConsent(consentSettings);
	Cookies.set("cookie_consent", "all", { expires: 365 });
	// Usuń ciasteczko ze szczegółami, jeśli istniało
	Cookies.remove(COOKIE_DETAILS_NAME);
	Cookies.remove(COOKIE_DETAILS_NAME, { path: "/" }); // Na wszelki wypadek

	return consentSettings;
};

// Ustaw tylko niezbędne zgody
const grantNecessaryConsent = () => {
	const consentSettings = {
		[CONSENT_TYPES.AD_STORAGE]: "denied",
		[CONSENT_TYPES.AD_USER_DATA]: "denied",
		[CONSENT_TYPES.AD_PERSONALIZATION]: "denied",
		[CONSENT_TYPES.ANALYTICS_STORAGE]: "denied",
		[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: "granted",
		[CONSENT_TYPES.SECURITY_STORAGE]: "granted",
	};

	updateGoogleConsent(consentSettings);
	Cookies.set("cookie_consent", "necessary", { expires: 365 });
	// Usuń ciasteczko ze szczegółami, jeśli istniało
	Cookies.remove(COOKIE_DETAILS_NAME);
	Cookies.remove(COOKIE_DETAILS_NAME, { path: "/" }); // Na wszelki wypadek

	// Usuń istniejące cookies analityczne
	removeAnalyticsCookies();

	return consentSettings;
};

// Ustaw niestandardowe zgody na podstawie wyborów użytkownika
const grantCustomConsent = (customSettings) => {
	// Walidacja wejścia (upewnijmy się, że mamy boolean)
	const validatedSettings = {
		[CONSENT_TYPES.AD_STORAGE]: !!customSettings[CONSENT_TYPES.AD_STORAGE],
		[CONSENT_TYPES.AD_USER_DATA]:
			!!customSettings[CONSENT_TYPES.AD_USER_DATA],
		[CONSENT_TYPES.AD_PERSONALIZATION]:
			!!customSettings[CONSENT_TYPES.AD_PERSONALIZATION],
		[CONSENT_TYPES.ANALYTICS_STORAGE]:
			!!customSettings[CONSENT_TYPES.ANALYTICS_STORAGE],
		[CONSENT_TYPES.FUNCTIONALITY_STORAGE]:
			!!customSettings[CONSENT_TYPES.FUNCTIONALITY_STORAGE],
		[CONSENT_TYPES.SECURITY_STORAGE]:
			!!customSettings[CONSENT_TYPES.SECURITY_STORAGE],
	};

	const consentSettings = {
		[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: "granted",
		[CONSENT_TYPES.SECURITY_STORAGE]: "granted",
		[CONSENT_TYPES.AD_STORAGE]: validatedSettings[CONSENT_TYPES.AD_STORAGE]
			? "granted"
			: "denied",
		[CONSENT_TYPES.AD_USER_DATA]: validatedSettings[
			CONSENT_TYPES.AD_USER_DATA
		]
			? "granted"
			: "denied",
		[CONSENT_TYPES.AD_PERSONALIZATION]: validatedSettings[
			CONSENT_TYPES.AD_PERSONALIZATION
		]
			? "granted"
			: "denied",
		[CONSENT_TYPES.ANALYTICS_STORAGE]: validatedSettings[
			CONSENT_TYPES.ANALYTICS_STORAGE
		]
			? "granted"
			: "denied",
	};

	updateGoogleConsent(consentSettings);
	// Zapisz główny status 'custom'
	Cookies.set("cookie_consent", "custom", { expires: 365 });
	// Zapisz szczegółowe ustawienia jako JSON string
	Cookies.set(COOKIE_DETAILS_NAME, JSON.stringify(validatedSettings), {
		expires: 365,
	});

	// Jeśli zgoda na analitykę została odrzucona, usuń ciasteczka
	if (consentSettings[CONSENT_TYPES.ANALYTICS_STORAGE] === "denied") {
		removeAnalyticsCookies();
	}

	return consentSettings;
};

// Inicjalizacja zgód na podstawie zapisanych preferencji
const initializeConsent = () => {
	const existingConsent = getConsentValue();

	if (existingConsent === "all") {
		return grantFullConsent();
	} else if (existingConsent === "custom") {
		const customSettings = getStoredCustomSettings();
		if (customSettings) {
			// Mamy zapisane ustawienia, zastosuj je
			return grantCustomConsent(customSettings);
		} else {
			// Ciasteczko 'custom', ale brak szczegółów - bezpieczny fallback
			// eslint-disable-next-line no-console
			console.warn(
				"Custom consent cookie found, but details are missing or invalid. Falling back to necessary."
			);
			return grantNecessaryConsent();
		}
	} else if (existingConsent === "necessary") {
		return grantNecessaryConsent();
	}

	// Domyślnie brak zgody na śledzenie (banner powinien się pojawić)
	return null;
};

export const consentService = {
	CONSENT_TYPES,
	getConsentValue,
	getStoredCustomSettings,
	grantFullConsent,
	grantNecessaryConsent,
	grantCustomConsent,
	initializeConsent,
	removeAnalyticsCookies,
};

export default consentService;
